import { configureStore } from '@reduxjs/toolkit';
import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { get } from 'lodash';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { MessagePlugin } from 'tdesign-react';

import { getDomain } from '@/utils/getApiDomain';
import { transformObjectDeep } from '@/utils/tans/object';

import * as reducer from './modules';

const domain = getDomain();

const baseQuery = fetchBaseQuery({
  mode: 'cors',
  credentials: 'include',
  baseUrl: domain,
});

const baseQueryWrapper: BaseQueryFn<
string | FetchArgs,
unknown,
FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseQuery(
    transformObjectDeep(args, 'snake'),
    api,
    extraOptions,
  );
  const res: any = transformObjectDeep(result, 'camel');
  const code = get(res, 'data.code') || 0;
  const message = get(res, 'data.msg');
  if (code !== 0 && message) {
    MessagePlugin.error(message);
  }
  return result.error === undefined && code === 0
    ? res
    : {
      ...res,
      error: { status: code, errmsg: message, data: null },
      data: undefined,
    };
};

export const api = createApi({
  reducerPath: 'api',
  refetchOnMountOrArgChange: true,
  baseQuery: baseQueryWrapper,
  endpoints: () => ({}),
});

export const store = configureStore({
  reducer: {
    [api.reducerPath]: api.reducer,
    ...reducer,
  },
  middleware: getDefaultMiddleware => getDefaultMiddleware({ serializableCheck: false }).concat(api.middleware),
});

export type AppDispatch = typeof store.dispatch;
export const useAppDispatch: () => AppDispatch = useDispatch;
export type RootState = ReturnType<typeof store.getState>;
export const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;

export default store;
